import { CurrencyView } from '../currency-view';
import { BlockLoading } from '../fetch-status/block-loading';
import { EndStatus } from '../fetch-status/end-status';
import { PositionNothing } from '../fetch-status/position-nothing';
import { FormatAfterDate } from '../format/format-after-date';
import { EventOutcomeName, EventPrimaryTitle } from '../market-event-title';
import { OptionsPositionBadge } from '../options-positions-badge';
import { PendingTradeFeedRow } from '../pending-trade/pending-trade-rows';
import { TextTooltip } from '../text-tooltip';
import { Separator } from '../ui/separator';
import { UploadedImage } from '../uploaded-image';
import { IS_DEBUG } from '@/client/confs/app';
import { useAppSettingStore } from '@/client/store/app-store';
import { useModelStore } from '@/client/store/model-store';
import { usePendingTradesFor } from '@/client/store/pending-trades-store';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { eventPrimaryTitleText } from '@/client/utils/i18n-content';
import { useEventLabels } from '@/client/hooks/use-event-labels';
import useLocale from '@/client/hooks/use-locale';
import { useUserTrades } from '@/libs/react-sdk/api/use-user-trades';
import { OrderSide, UserOrderInfo } from '@/libs/sdk/api';
import useInViewport from 'ahooks/lib/useInViewport';
import { BasicTarget } from 'ahooks/lib/utils/domTarget';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';

export const TradeItemDT = ({
  label,
  value,
  decimal = 6,
  isChange = false,
}: {
  label: string;
  value: any;
  decimal?: number;
  isChange?: boolean;
}) => {
  const isUp = Number(value) > -1 / Math.pow(10, 2);
  return (
    <div className="flex flex-col">
      <div className="text-3 leading-[normal] font-medium text-foreground/40">{label}</div>
      <div
        className={cn(
          'flex items-center gap-.5',
          isChange ? (isUp ? 'text-[#3CBD2C]' : 'text-[#F65570]') : 'text-foreground'
        )}
      >
        <CurrencyView
          isChange={isChange}
          value={value}
          decimal={decimal}
          precision={2}
          className={cn(
            'text-3 leading-[normal] font-medium',
            isChange ? (isUp ? 'text-[#3CBD2C]' : 'text-[#F65570]') : 'text-foreground'
          )}
        />
      </div>
    </div>
  );
};

type TradeItemProps = {
  className?: string;
  root?: BasicTarget<Element>;
  onViewChange?: (isInView: boolean) => void;
  order: UserOrderInfo;
};
export const TradeItem = ({ className, root, onViewChange, order }: TradeItemProps) => {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const ref = useRef<HTMLDivElement>(null);
  const setTradeOpen = useModelStore(state => state.setTradeOpen);
  useInViewport(ref, {
    root,
    callback: entry => onViewChange?.(entry.isIntersecting),
  });
  const optionsAmount = useMemo(() => {
    if (order.side === OrderSide.deposit) {
      return Number(order.receiveAmount);
    }
    if (order.side === OrderSide.withdraw) {
      return Number(order.amount);
    }
    return 0;
  }, [order]);
  const labels = useEventLabels([order.option.marketAddress]);
  const label = labels[order.option.marketAddress.toLowerCase()];
  const { locale } = useLocale();
  return (
    <div
      ref={ref}
      onClick={() => {
        navigate(`/market/${order.option.marketAddress}`);
      }}
      className={cn(
        'min-h-[137px] py-3 ps-4 pe-3 border border-foreground/10 rounded-4 hover:bg-foreground/5 cursor-pointer bg-card',
        className
      )}
    >
      <div className="flex items-center gap-3">
        <UploadedImage fileKey={order.option.marketPicUrl} className="size-9 rounded-2" />
        <div className="space-y-1 flex-1">
          <TextTooltip
            tooltip={eventPrimaryTitleText(label, order.option.marketName, locale)}
            className="font-medium line-clamp-1 text-start market-title-hover"
          >
            <EventPrimaryTitle label={label} marketName={order.option.marketName} />
          </TextTooltip>
          {order.option && (
            <div className="flex items-center gap-1.5">
              <EventOutcomeName
                label={label}
                marketName={order.option.marketName}
                className="text-xs text-foreground/70 line-clamp-1 flex-shrink-0"
              />
              <OptionsPositionBadge
                follow={false}
                options={[
                  {
                    type: order.side == OrderSide.deposit ? 'buy' : 'sell',
                    marketAddress: order.option.marketAddress,
                    optionAddress: order.option.address,
                    label: order.option.description ?? order.option.name,
                    value: optionsAmount,
                    decimal: order.option.decimal,
                    amount: BigInt(optionsAmount),
                  },
                ]}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  setTradeOpen(true);
                }}
              />
            </div>
          )}
        </div>
      </div>
      <Separator orientation="vertical" className="h-[1px] w-full my-4 bg-foreground/4 " />
      <div className="flex gap-3 justify-between items-center">
        <div className="flex flex-col">
          <div className="text-3 leading-[normal] font-medium text-foreground/40">{t('type')}</div>
          <div>
            {order.side === OrderSide.deposit && t('buy')}
            {order.side === OrderSide.withdraw && t('sell')}
          </div>
        </div>
        <TradeItemDT label={t('price')} value={order.dealPrice} decimal={order.option.decimal} />
        <TradeItemDT label={t('value')} value={optionsAmount} decimal={order.option.decimal} />
        <div className="flex flex-col">
          <div className="text-3 leading-[normal] font-medium text-foreground/40">{t('date')}</div>
          <div>
            <FormatAfterDate time={order.timestamp} />
          </div>
        </div>
      </div>
    </div>
  );
};

type TradesProps = {
  uid: string;
  className?: string;
};
export const Trades = ({ uid, className }: TradesProps) => {
  const baseTokenAddress = useAppSettingStore(state => state.baseToken.address);
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useUserTrades(api, {
    baseTokenAddress,
    page: 1,
    pageSize: 5,
    uid,
  });
  const orderList = useMemo(() => data?.pages.flatMap(item => item.orders) ?? [], [data]);
  const pendingList = usePendingTradesFor(useMemo(() => ({ uid }), [uid]));
  const moreLastIndex = useMemo(() => orderList.length - 2, [orderList.length]);
  const loadMoreItems = async (isInView: boolean, index: number) => {
    if (isInView && index === moreLastIndex && hasNextPage && !isFetchingNextPage) {
      if (IS_DEBUG) console.log('load more history', uid, moreLastIndex, isInView, index);
      await fetchNextPage();
    }
  };
  return (
    <div className={cn('w-full', className)}>
      <div className="space-y-4">
        {pendingList.map(pt => (
          <PendingTradeFeedRow key={pt.txHash} trade={pt} />
        ))}
        {orderList.map((item, index) => (
          <TradeItem key={`history-${index}`} order={item} onViewChange={isInView => loadMoreItems(isInView, index)} />
        ))}
      </div>
      {(isLoading || isFetchingNextPage) && <BlockLoading />}
      {!isLoading && !hasNextPage && orderList.length === 0 && pendingList.length === 0 && <PositionNothing />}
      {!isLoading && !hasNextPage && orderList.length > 0 && <EndStatus />}
    </div>
  );
};
